import type {UseAuthResult} from '../hooks/useAuth'

export const ROS_UI_FEATURE_KEYS = [
  'reachy-enabled',
  'ros-chat-general-purpose',
  'reachy-agent-builder',
  'ros-registry',
  'ros-skills',
  'ros-tools',
  'ros-slack',
  'authoring-agent-enabled',
  'crosstabs-agent-enabled',
  'website-builder-agent-enabled',
  'artifacts-mgmt',
  'app-artifacts',
  'ros-dev-mode',
] as const

export type RosUiFeatureKey = (typeof ROS_UI_FEATURE_KEYS)[number]
export type RosUiFeatureFlags = Record<RosUiFeatureKey, boolean>

export interface RosUiFeatureSnapshot {
  userType: 'internal' | 'external'
  flags: RosUiFeatureFlags
  evaluatedAt: string
}

export type RosFeatureRefreshReason = 'initial' | 'token_refresh' | 'focus' | 'forbidden' | 'manual'

export interface RosAuthFetchOptions {
  /** Background, best-effort probes must not surface an entitlement toast for an expected 403. */
  silentForbidden?: boolean
}

export type RosFeatureState =
  | {status: 'loading'; snapshot: null}
  | {status: 'ready' | 'denied' | 'error'; snapshot: RosUiFeatureSnapshot}

export interface RosFeatureContextValue {
  state: RosFeatureState
  snapshot: RosUiFeatureSnapshot
  auth: UseAuthResult
  enabled: (key: RosUiFeatureKey) => boolean
  internalGeneralPurpose: boolean
  refreshFeatures: (reason?: RosFeatureRefreshReason) => Promise<void>
  authFetch: (input: string, init?: RequestInit, options?: RosAuthFetchOptions) => Promise<Response>
  reportUnavailable: () => void
}

export function allRosUiFeaturesDisabled(userType: 'internal' | 'external' = 'external'): RosUiFeatureSnapshot {
  return {
    userType,
    flags: Object.fromEntries(ROS_UI_FEATURE_KEYS.map(key => [key, false])) as RosUiFeatureFlags,
    evaluatedAt: new Date(0).toISOString(),
  }
}

export function parseRosUiFeatureSnapshot(value: unknown): RosUiFeatureSnapshot | null {
  if (!value || typeof value !== 'object') return null
  const candidate = value as {userType?: unknown; flags?: unknown; evaluatedAt?: unknown}
  if ((candidate.userType !== 'internal' && candidate.userType !== 'external')
    || !candidate.flags || typeof candidate.flags !== 'object' || Array.isArray(candidate.flags)
    || typeof candidate.evaluatedAt !== 'string') return null
  const rawFlags = candidate.flags as Record<string, unknown>
  if (ROS_UI_FEATURE_KEYS.some(key => typeof rawFlags[key] !== 'boolean')) return null
  return {
    userType: candidate.userType,
    flags: Object.fromEntries(ROS_UI_FEATURE_KEYS.map(key => [key, rawFlags[key] === true])) as RosUiFeatureFlags,
    evaluatedAt: candidate.evaluatedAt,
  }
}
